import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import promediaService from '../services/promediaService';
import './RemoteControlModal.css';

// 2026-05-18 — match the device-modal keypad style 1:1. Square colored button
// with an SVG glyph + a small grey label below it. `wide` doubles the cell
// width (spans 2 grid columns) for the Power row's emergency-button layout.
function Rc2Key({ icon, label, color, onClick, disabled, wide, title }) {
  return (
    <div className={`rc2-cell ${wide ? 'wide-2' : ''}`}>
      <button
        type="button"
        className="rc2-key"
        style={{ background: disabled ? '#cfcfcf' : color, color: '#fff' }}
        onClick={onClick}
        disabled={disabled}
        title={title || label}
        aria-label={label}
      >{icon}</button>
      <div className="rc2-cell-label">{label}</div>
    </div>
  );
}

// SVG glyphs lifted from RemoteControlModal so the two modals look identical.
// Module-scoped (not per-render) so React doesn't re-create the elements on
// every re-render — small perf, also matches what the user sees on the
// device-modal page.
const GLYPHS = {
  arrLeft:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  arrRight: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  arrUp:    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>,
  arrDown:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
  home:     <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  menu:     <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
  back:     <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  ok:       <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  rotate:   <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  reboot:   <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7"/><polyline points="21 3 21 9 15 9"/></svg>,
  moonZ:    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
  power:    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18.36 6.64A9 9 0 0 1 20.77 15"/><path d="M6.64 6.64A9 9 0 1 0 12 21a9 9 0 0 0 5.36-1.78"/><line x1="12" y1="2" x2="12" y2="12"/></svg>,
  keyboard: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="6" y1="10" x2="6" y2="10"/><line x1="10" y1="10" x2="10" y2="10"/><line x1="14" y1="10" x2="14" y2="10"/><line x1="18" y1="10" x2="18" y2="10"/><line x1="7" y1="14" x2="17" y2="14"/></svg>,
};

const GroupControlModal = ({ groups, initialGroupId, onClose }) => {
  const [selectedGroupId, setSelectedGroupId] = useState(initialGroupId || '');
  const [groupDetails, setGroupDetails] = useState(null);
  const [allDevices, setAllDevices] = useState([]);

  const [isConnected, setIsConnected] = useState(false);
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(false);

  const [keyboardText, setKeyboardText] = useState('');
  const [showKeyboardInput, setShowKeyboardInput] = useState(false);

  const [lastActionLabel, setLastActionLabel] = useState('');
  const [lastActionSummary, setLastActionSummary] = useState('');
  const [sleepActive, setSleepActive] = useState(false);

  const [volume, setVolume] = useState(50);
  const [textMessage, setTextMessage] = useState('');
  const [displayLength, setDisplayLength] = useState(10);

  const [groupFiles, setGroupFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');

  // 2026-05-18 — feature parity with RemoteControlModal: layouts list (probed
  // from the first online device since group members share the same layouts),
  // the selected layout, and the media-playback target. Playing/pausing the
  // 'background' track is a global broadcast (promediaService.pauseBackground
  // etc); playing/pausing 'main' zone is per-device via runForOnlineDevices.
  const [layouts, setLayouts] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState('');
  const [mediaControlTarget, setMediaControlTarget] = useState('background');
  const [isPaused, setIsPaused] = useState(false);

  const [liveScreenshot, setLiveScreenshot] = useState(null);
  const [deviceResolution, setDeviceResolution] = useState({ width: null, height: null });

  const volumeTimeoutRef = useRef(null);
  const liveViewRef = useRef(null);
  const dragStartRef = useRef(null);
  const keyboardInputRef = useRef(null);

  const selectedGroup = useMemo(() => {
    return (groups || []).find(g => String(g.id) === String(selectedGroupId)) || null;
  }, [groups, selectedGroupId]);

  const deviceNameSet = useMemo(() => {
    const names = groupDetails?.devices;
    if (!Array.isArray(names)) return new Set();
    return new Set(names);
  }, [groupDetails]);

  const groupDevices = useMemo(() => {
    // 2026-05-19 Phase D — group_devices.device_name now holds device_uid
    // (FK target changed). Filter allDevices by uid first, falling back to
    // device_name for any legacy row where uid wasn't backfilled.
    if (!deviceNameSet.size) return [];
    return allDevices.filter(d =>
      deviceNameSet.has(d.device_uid) || deviceNameSet.has(d.device_name)
    );
  }, [allDevices, deviceNameSet]);

  const onlineDevices = useMemo(() => groupDevices.filter(d => d.is_online), [groupDevices]);
  const offlineDevices = useMemo(() => groupDevices.filter(d => !d.is_online), [groupDevices]);

  const screenshotDevice = useMemo(() => {
    return onlineDevices.length > 0 ? onlineDevices[0] : null;
  }, [onlineDevices]);

  const fetchGroupDetails = useCallback(async (groupId) => {
    if (!groupId) return;
    try {
      const resp = await fetch(`/api/groups/${groupId}`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Failed to load group details');
      const details = await resp.json();
      setGroupDetails(details);
    } catch (e) {
      console.error('GroupControlModal: group details error:', e);
      setStatus(`❌ ${e.message}`);
      setGroupDetails(null);
    }
  }, []);

  const fetchDevices = useCallback(async () => {
    try {
      const resp = await fetch('/api/devices', { credentials: 'include' });
      if (!resp.ok) throw new Error('Failed to load devices');
      const data = await resp.json();
      setAllDevices(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('GroupControlModal: devices error:', e);
      setStatus(`❌ ${e.message}`);
      setAllDevices([]);
    }
  }, []);

  const fetchGroupFiles = useCallback(async (groupId) => {
    if (!groupId) return;
    try {
      const resp = await fetch(`/api/groups/${groupId}/files`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Failed to load group files');
      const data = await resp.json();
      setGroupFiles(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('GroupControlModal: group files error:', e);
      setGroupFiles([]);
    }
  }, []);

  const connectIfNeeded = useCallback(async () => {
    try {
      await promediaService.connect();
      setIsConnected(true);
      return true;
    } catch (e) {
      setIsConnected(false);
      setStatus(`❌ MQTT connect failed: ${e.message}`);
      return false;
    }
  }, []);

  useEffect(() => {
    connectIfNeeded();

    return () => {
      if (volumeTimeoutRef.current) {
        clearTimeout(volumeTimeoutRef.current);
        volumeTimeoutRef.current = null;
      }
    };
  }, [connectIfNeeded]);

  useEffect(() => {
    if (showKeyboardInput && keyboardInputRef.current) {
      keyboardInputRef.current.focus();
    }
  }, [showKeyboardInput]);

  useEffect(() => {
    if (!selectedGroupId) return;
    fetchGroupDetails(selectedGroupId);
    fetchGroupFiles(selectedGroupId);
  }, [selectedGroupId, fetchGroupDetails, fetchGroupFiles]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    if (!selectedGroupId) return;
    setSelectedFile('');
    setStatus('');
    setLastActionLabel('');
    setLastActionSummary('');
    setSleepActive(false);
  }, [selectedGroupId]);

  const detectSleepState = useCallback(async () => {
    if (!selectedGroupId) return;
    if (onlineDevices.length === 0) {
      setSleepActive(false);
      return;
    }

    const ok = isConnected ? true : await connectIfNeeded();
    if (!ok) return;

    try {
      setStatus('');
      setLastActionLabel('Sleep');
      setLastActionSummary('...');

      // Determine the canonical sleep layout name from one online device.
      // 2026-05-19 Phase C — route by device_uid (the agent's MQTT topic).
      const firstDevice = onlineDevices[0];
      promediaService.setDevice(firstDevice.device_uid || firstDevice.device_name);

      const layoutsInfo = await promediaService.getLayouts().catch(() => null);
      const layouts = layoutsInfo?.result;

      let sleepLayoutName = 'sleep';
      if (Array.isArray(layouts) && layouts.length > 0) {
        const entry = layouts.find((l) => {
          if (!l) return false;
          if (typeof l === 'string') return l.toLowerCase() === 'sleep';
          if (l.name) return String(l.name).toLowerCase() === 'sleep';
          return false;
        });
        if (entry) {
          sleepLayoutName = typeof entry === 'string' ? entry : entry.name;
        }
      }

      let inSleep = 0;
      let checked = 0;
      const errors = [];

      for (const device of onlineDevices) {
        try {
          promediaService.setDevice(device.device_uid || device.device_name);
          const current = await promediaService.getCurrentLayout().catch(() => null);
          const currentLayout = current?.result?.currentLayout;
          checked += 1;

          const isSleep = !!(
            currentLayout &&
            String(currentLayout).toLowerCase() === String(sleepLayoutName).toLowerCase()
          );
          if (isSleep) inSleep += 1;
        } catch (e) {
          errors.push(`${device.device_name}: ${e.message}`);
        }
      }

      const total = onlineDevices.length;

      const allSleep = checked > 0 && inSleep === checked;
      const noneSleep = checked > 0 && inSleep === 0;

      setSleepActive(inSleep > 0);

      if (errors.length > 0) {
        let message = `Sleep status errors:\n${errors.slice(0, 5).join('\n')}`;
        if (errors.length > 5) message += `\n... and ${errors.length - 5} more`;
        setStatus(message);
      }

      if (allSleep) {
        setLastActionSummary(`In sleep ✅ ${inSleep}/${total}`);
      } else if (noneSleep) {
        setLastActionSummary(`Active ✅ ${total}/${total}`);
      } else {
        setLastActionSummary(`Mixed ⚠️ ${inSleep}/${total}`);
      }
    } catch (e) {
      // ignore
    }
  }, [connectIfNeeded, isConnected, onlineDevices, selectedGroupId]);

  useEffect(() => {
    if (!selectedGroupId) return;
    if (onlineDevices.length === 0) return;
    detectSleepState();
  }, [detectSleepState, onlineDevices.length, selectedGroupId]);

  // 2026-05-18 — populate the Layouts dropdown once the group has at least
  // one online device. We probe the first online member; group siblings run
  // the same agent build and therefore share the same named layouts.
  useEffect(() => {
    if (!isConnected) return;
    if (onlineDevices.length === 0) { setLayouts([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const probe = onlineDevices[0];
        const resp = await promediaService.getLayouts(probe.device_name);
        if (cancelled) return;
        const list = Array.isArray(resp?.result) ? resp.result : [];
        setLayouts(list);
      } catch (_e) {
        if (!cancelled) setLayouts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isConnected, onlineDevices, selectedGroupId]);

  useEffect(() => {
    if (!screenshotDevice || !isConnected) {
      setLiveScreenshot(null);
      setDeviceResolution({ width: null, height: null });
      return;
    }

    let cancelled = false;

    const loop = async () => {
      while (!cancelled && isConnected) {
        try {
          promediaService.setDevice(screenshotDevice.device_uid || screenshotDevice.device_name);
          const result = await promediaService.getScreenshot();
          const base64Data = result?.result?.data;
          if (base64Data && base64Data.length > 1000) {
            setLiveScreenshot(`data:image/png;base64,${base64Data}`);
          }
        } catch (e) {
          await new Promise(r => setTimeout(r, 500));
        }

        await new Promise(r => setTimeout(r, 250));
      }
    };

    loop();

    return () => {
      cancelled = true;
    };
  }, [screenshotDevice, isConnected]);

  useEffect(() => {
    if (!liveScreenshot) return;
    if (deviceResolution.width && deviceResolution.height) return;

    try {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight && img.naturalWidth >= 200 && img.naturalHeight >= 200) {
          setDeviceResolution({ width: img.naturalWidth, height: img.naturalHeight });
        }
      };
      img.src = liveScreenshot;
    } catch {
      // ignore
    }
  }, [liveScreenshot, deviceResolution.width, deviceResolution.height]);

  const runForOnlineDevices = useCallback(async (label, fn) => {
    if (!isConnected) {
      const ok = await connectIfNeeded();
      if (!ok) return;
    }

    if (onlineDevices.length === 0) {
      setStatus('⚫ No online devices in this group');
      setLastActionLabel(label);
      setLastActionSummary('⚫ 0/0');
      return;
    }

    if (isLoading || operationInProgress) return;

    try {
      setIsLoading(true);
      setOperationInProgress(true);
      setStatus('');
      setLastActionLabel(label);
      setLastActionSummary('...');

      let success = 0;
      const errors = [];

      for (const device of onlineDevices) {
        try {
          promediaService.setDevice(device.device_uid || device.device_name);
          await fn(device);
          success += 1;
        } catch (e) {
          errors.push(`${device.device_name}: ${e.message}`);
        }
      }

      const failed = errors.length;
      const summary = `${success}/${onlineDevices.length}`;
      setLastActionLabel(label);
      setLastActionSummary(failed > 0 ? `⚠️ ${summary}` : `✅ ${summary}`);

      if (failed > 0) {
        let message = `${label} errors:\n${errors.slice(0, 5).join('\n')}`;
        if (errors.length > 5) message += `\n... and ${errors.length - 5} more`;
        setStatus(message);
      }

      return { success, total: onlineDevices.length, failed };
    } finally {
      setIsLoading(false);
      setOperationInProgress(false);
    }
  }, [connectIfNeeded, isConnected, isLoading, onlineDevices, operationInProgress]);

  const sendKeyEvent = async (keyCode, label) => {
    const keyLabel = label || String(keyCode);
    await runForOnlineDevices(`Key: ${keyLabel}`, async () => {
      await promediaService.executeShell(`input keyevent ${keyCode}`);
    });
  };

  // 2026-05-18 — power off every online device in the group. `reboot -p` is
  // the Android shell shutdown command; with -p the device fully powers off
  // (no auto-reboot). Confirm dialog is required because this is the only
  // action in the modal that takes devices off-network entirely.
  const handlePowerButton = async () => {
    const n = onlineDevices.length;
    if (n === 0) {
      setStatus('⚫ No online devices in this group');
      return;
    }
    if (!window.confirm(`Power off ${n} device${n === 1 ? '' : 's'} in this group? They will need to be turned on manually.`)) return;
    await runForOnlineDevices('Power off', async () => {
      await promediaService.executeShell('reboot -p');
    });
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);

    if (volumeTimeoutRef.current) {
      clearTimeout(volumeTimeoutRef.current);
    }

    volumeTimeoutRef.current = setTimeout(async () => {
      const androidVolume = Math.round((newVolume / 100) * 15);
      await runForOnlineDevices(`Volume ${newVolume}%`, async () => {
        await promediaService.setVolume(androidVolume);
      });
    }, 500);
  };

  const handleDeviceAction = async (action) => {
    switch (action) {
      case 'Reload App':
        await runForOnlineDevices('Reload', async () => promediaService.reloadApp());
        return;
      case 'Reboot Device':
        await runForOnlineDevices('Reboot', async () => promediaService.rebootDevice());
        return;
      case 'Toggle Sleep':
        if (sleepActive) {
          const res = await runForOnlineDevices('Exit sleep', async () => {
            await promediaService.clearLayout();
          });
          if (res && res.success === res.total) setSleepActive(false);
        } else {
          const res = await runForOnlineDevices('Enter sleep', async () => {
            const layoutsInfo = await promediaService.getLayouts().catch(() => null);
            const layouts = layoutsInfo?.result;

            let layoutName = 'sleep';
            if (Array.isArray(layouts) && layouts.length > 0) {
              const entry = layouts.find((l) => {
                if (!l) return false;
                if (typeof l === 'string') return l.toLowerCase() === 'sleep';
                if (l.name) return String(l.name).toLowerCase() === 'sleep';
                return false;
              });
              if (entry) {
                layoutName = typeof entry === 'string' ? entry : entry.name;
              } else {
                throw new Error('sleep layout not available');
              }
            }

            await promediaService.setLayout(layoutName);
          });
          if (res && res.success === res.total) setSleepActive(true);
        }
        return;
      case 'Play Beep':
        await runForOnlineDevices('Beep', async () => promediaService.playBeep());
        return;
      default:
        break;
    }
  };

  const sendKeyboardText = async () => {
    const text = keyboardText.trim();
    if (!text) return;

    // Encode spaces as %s for input text command and escape single quotes
    const encoded = text.replace(/'/g, "\\'").replace(/\s+/g, '%s');
    await runForOnlineDevices(`Keyboard: "${text}"`, async () => {
      await promediaService.executeShell(`input text '${encoded}'`);
    });

    setKeyboardText('');
  };

  const handleKeyboardKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendKeyboardText();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowKeyboardInput(false);
      setKeyboardText('');
    }
  };

  const handleShowMessage = async () => {
    const msg = textMessage.trim();
    if (!msg) {
      setStatus('Please enter a message');
      return;
    }

    const html = `<div style="display: flex; align-items: center; justify-content: center; height: 100vh; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); font-family: Arial, sans-serif;">
      <h1 style="color: white; font-size: 4em; text-align: center; text-shadow: 2px 2px 4px rgba(0,0,0,0.5); padding: 40px; border-radius: 20px; background: rgba(255,255,255,0.1); backdrop-filter: blur(10px);">
        ${msg}
      </h1>
    </div>`;

    await runForOnlineDevices('Show Message', async () => {
      await promediaService.showHtml(html, displayLength, 'Whole screen');
    });
  };

  const handleShowFile = async () => {
    if (!selectedFile) {
      setStatus('Please select a file');
      return;
    }

    await runForOnlineDevices('Display Now', async () => {
      await promediaService.showFile(selectedFile, displayLength, 'Whole screen');
    });
  };

  // 2026-05-18 — set the same named layout on every online device in the
  // group. Layouts are by-name strings (e.g. "sleep", "Default Layout 1");
  // groups share a layout catalog because all member devices run the same
  // Promedia agent build.
  const handleSetLayout = async () => {
    if (!selectedLayout) {
      setStatus('Please select a layout');
      return;
    }
    await runForOnlineDevices(`Layout: ${selectedLayout}`, async () => {
      await promediaService.setLayout(selectedLayout);
    });
  };

  // Clear → revert each device to whatever its scheduler says it should show.
  const handleClearLayout = async () => {
    await runForOnlineDevices('Back to scheduled', async () => {
      await promediaService.clearLayout();
    });
    setSelectedLayout('');
  };

  // Media-playback dispatcher matching RemoteControlModal's `handleMediaControl`.
  // Background-music ops are global MQTT broadcasts (one call covers every
  // device); main-zone ops go per-device through runForOnlineDevices.
  const handleMediaControl = async (action) => {
    if (operationInProgress || isLoading) return;
    if (!isConnected) {
      const ok = await connectIfNeeded();
      if (!ok) return;
    }
    try {
      setIsLoading(true);
      setOperationInProgress(true);

      if (mediaControlTarget === 'background') {
        let result;
        if (action === 'prev') {
          setLastActionLabel('BG Music: Previous');
          result = await promediaService.playPreviousBackground();
        } else if (action === 'next') {
          setLastActionLabel('BG Music: Next');
          result = await promediaService.playNextBackground();
        } else {
          if (isPaused) {
            setLastActionLabel('BG Music: Resume');
            result = await promediaService.resumeBackground();
            setIsPaused(false);
          } else {
            setLastActionLabel('BG Music: Pause');
            result = await promediaService.pauseBackground();
            setIsPaused(true);
          }
        }
        setLastActionSummary(result && result.success !== false ? '✅' : '⚠️');
        return;
      }

      // Main-zone playback — issue per-device. Pause/Resume toggle uses the
      // isPaused flag on this modal as the local truth source (matches the
      // device-modal behavior); the actual device state may briefly diverge
      // until the next status fetch but the UX is instant.
      const label = action === 'prev' ? 'Play Previous'
                   : action === 'next' ? 'Play Next'
                   : (isPaused ? 'Resume' : 'Pause');
      await runForOnlineDevices(label, async () => {
        if (action === 'prev')      await promediaService.playPrevious();
        else if (action === 'next') await promediaService.playNext();
        else if (isPaused)          await promediaService.resumePlayback();
        else                        await promediaService.pausePlayback();
      });
      if (action === 'play_pause') setIsPaused(p => !p);
    } finally {
      setIsLoading(false);
      setOperationInProgress(false);
    }
  };

  const handleLiveViewTap = async (event) => {
    const imgSource = liveScreenshot;
    if (!imgSource) return;

    if (!deviceResolution.width || !deviceResolution.height) {
      setStatus('⚠️ Screen resolution unknown, cannot send touch');
      return;
    }

    if (!liveViewRef.current) return;

    const rect = liveViewRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    const ratioX = rect.width > 0 ? clickX / rect.width : 0;
    const ratioY = rect.height > 0 ? clickY / rect.height : 0;

    const touchX = Math.round(ratioX * deviceResolution.width);
    const touchY = Math.round(ratioY * deviceResolution.height);

    await runForOnlineDevices(`Touch ${touchX}x${touchY}`, async () => {
      await promediaService.executeShell(`input tap ${touchX} ${touchY}`);
    });
  };

  const handleLiveViewMouseDown = (event) => {
    if (!liveViewRef.current) return;
    const rect = liveViewRef.current.getBoundingClientRect();
    dragStartRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      rect
    };
  };

  const handleLiveViewMouseUp = async (event) => {
    const start = dragStartRef.current;
    dragStartRef.current = null;
    if (!start || !liveViewRef.current) return;

    const dx = event.clientX - start.clientX;
    const dy = event.clientY - start.clientY;
    const distanceSquared = dx * dx + dy * dy;

    if (distanceSquared < 25) {
      await handleLiveViewTap(event);
      return;
    }

    const rect = start.rect;
    const endX = event.clientX - rect.left;
    const endY = event.clientY - rect.top;
    const startX = start.clientX - rect.left;
    const startY = start.clientY - rect.top;

    const startRatioX = rect.width > 0 ? startX / rect.width : 0;
    const startRatioY = rect.height > 0 ? startY / rect.height : 0;
    const endRatioX = rect.width > 0 ? endX / rect.width : 0;
    const endRatioY = rect.height > 0 ? endY / rect.height : 0;

    const x1 = Math.round(startRatioX * deviceResolution.width);
    const y1 = Math.round(startRatioY * deviceResolution.height);
    const x2 = Math.round(endRatioX * deviceResolution.width);
    const y2 = Math.round(endRatioY * deviceResolution.height);

    await runForOnlineDevices('Swipe', async () => {
      await promediaService.executeShell(`input swipe ${x1} ${y1} ${x2} ${y2} 250`);
    });
  };

  const totalCount = groupDevices.length;
  const onlineCount = onlineDevices.length;
  const offlineCount = offlineDevices.length;

  return (
    <div className="remote-control-modal-overlay" onClick={onClose}>
      <div className="remote-control-modal-content" onClick={e => e.stopPropagation()}>
        <div className="remote-control-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <h2 style={{ margin: 0, whiteSpace: 'nowrap' }}>🖥️ Group Control</h2>
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              disabled={isLoading}
              className="remote-control-select"
              style={{ maxWidth: '280px' }}
              title="Select group"
            >
              {(groups || []).map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
          <button className="remote-control-close-button" onClick={onClose}>×</button>
        </div>

        <div className="remote-control-modal-body">
          <div className="remote-control-status-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div className={`remote-control-connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
                Group Devices ({totalCount})
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', fontSize: '12px', fontWeight: 700 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#0D47A1' }}>
                  <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#0D47A1', display: 'inline-block' }} />
                  {onlineCount} online
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#B71C1C' }}>
                  <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#B71C1C', display: 'inline-block' }} />
                  {offlineCount} offline
                </span>
              </div>
            </div>
            <div className="remote-control-status-message">
              {status ? (
                <span style={{ whiteSpace: 'pre-line' }}>{status}</span>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedGroup ? selectedGroup.name : ''}
                  </span>
                  {lastActionSummary ? (
                    <span style={{ fontWeight: 700, color: '#1b5e20', whiteSpace: 'nowrap' }}>
                      {lastActionLabel ? `${lastActionLabel}: ` : ''}{lastActionSummary}
                    </span>
                  ) : (
                    <span />
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="remote-control-section">
            <div style={{ position: 'relative' }}>
              <div
                ref={liveViewRef}
                className="remote-control-live-view"
                onMouseDown={handleLiveViewMouseDown}
                onMouseUp={handleLiveViewMouseUp}
                style={{
                  marginTop: '8px',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  background: '#000',
                  cursor: 'crosshair',
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={screenshotDevice ? `Preview from ${screenshotDevice.device_name}` : 'No online devices'}
              >
                {liveScreenshot && (
                  <img
                    src={liveScreenshot}
                    alt="Live group view"
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                )}
              </div>

              {showKeyboardInput && (
                <div
                  style={{
                    position: 'absolute',
                    left: '12px',
                    right: '12px',
                    bottom: '12px',
                    background: 'rgba(0, 0, 0, 0.85)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '10px',
                    padding: '12px',
                    zIndex: 5
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, color: '#fff', fontSize: '14px' }}>⌨️ Keyboard Input</h4>
                    <button
                      onClick={() => {
                        setShowKeyboardInput(false);
                        setKeyboardText('');
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#fff',
                        fontSize: '18px',
                        cursor: 'pointer',
                        padding: '0 6px'
                      }}
                      title="Close"
                    >
                      ×
                    </button>
                  </div>

                  <input
                    ref={keyboardInputRef}
                    type="text"
                    value={keyboardText}
                    onChange={(e) => setKeyboardText(e.target.value)}
                    onKeyDown={handleKeyboardKeyPress}
                    placeholder="Type here and press Enter..."
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.1)',
                      color: '#fff',
                      outline: 'none',
                      marginBottom: '10px',
                      boxSizing: 'border-box'
                    }}
                  />

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={sendKeyboardText}
                      disabled={isLoading || !keyboardText.trim()}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: keyboardText.trim() ? '#4CAF50' : '#e0e0e0',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: keyboardText.trim() ? 'pointer' : 'not-allowed',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                      title="Send (Enter)"
                    >
                      📤 Send (Enter)
                    </button>
                    <button
                      onClick={() => setKeyboardText('')}
                      disabled={isLoading || !keyboardText}
                      style={{
                        padding: '10px 15px',
                        background: '#ff9800',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: keyboardText ? 'pointer' : 'not-allowed',
                        fontSize: '14px'
                      }}
                      title="Clear text"
                    >
                      🧹
                    </button>
                  </div>
                </div>
              )}
            </div>

            {deviceResolution.width && deviceResolution.height && (
              <div style={{ marginTop: '4px', fontSize: '11px', color: '#888', textAlign: 'center' }}>
                Device resolution: {deviceResolution.width} x {deviceResolution.height}
              </div>
            )}

            <div className="volume-slider-row" style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => sendKeyEvent(164, 'Mute')}
                style={{ padding: '6px 10px', borderRadius: '999px', border: 'none', background: '#F9A825', color: '#fff', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                🔇 Mute
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                disabled={isLoading}
                className="remote-control-volume-slider"
                style={{
                  flex: 1,
                  height: '10px',
                  borderRadius: '999px',
                  background: `linear-gradient(to right, #007bff 0%, #007bff ${volume}%, #e0e0e0 ${volume}%, #e0e0e0 100%)`,
                  appearance: 'none'
                }}
              />
              <span className="remote-control-volume-display" style={{ minWidth: '40px', textAlign: 'right', fontSize: '12px', color: '#555' }}>
                {volume}%
              </span>
              <button
                onClick={() => handleDeviceAction('Play Beep')}
                style={{ padding: '6px 10px', borderRadius: '999px', border: 'none', background: '#FBC02D', color: '#000', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                🔔 Beep
              </button>
            </div>

            {/* 2026-05-18 — keypad styled identically to the device modal.
                Three rows of four cells each, by owner ask:
                  Row 1: Left · Right · Up · Down   (slate grey #607D8B)
                  Row 2: Home · Menu · Back · OK    (slate / blue / green)
                  Row 3: Reload · Reboot · Sleep · Power (orange · amber · indigo · red)
                Keyboard toggle moved out of the grid to a small inline strip
                below, since the user-spec'd 12-cell arrangement leaves no room
                for it inside the rows. */}
            <div className="rc2-keypad-grid" style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', columnGap: '10px', rowGap: '14px' }}>
              <Rc2Key icon={GLYPHS.arrLeft}  label="Left"   color="#607D8B" onClick={() => sendKeyEvent(21, 'Left')}  disabled={isLoading} />
              <Rc2Key icon={GLYPHS.arrRight} label="Right"  color="#607D8B" onClick={() => sendKeyEvent(22, 'Right')} disabled={isLoading} />
              <Rc2Key icon={GLYPHS.arrUp}    label="Up"     color="#607D8B" onClick={() => sendKeyEvent(19, 'Up')}    disabled={isLoading} />
              <Rc2Key icon={GLYPHS.arrDown}  label="Down"   color="#607D8B" onClick={() => sendKeyEvent(20, 'Down')}  disabled={isLoading} />

              <Rc2Key icon={GLYPHS.home}     label="Home"   color="#546E7A" onClick={() => sendKeyEvent(3,  'Home')}  disabled={isLoading} />
              <Rc2Key icon={GLYPHS.menu}     label="Menu"   color="#546E7A" onClick={() => sendKeyEvent(82, 'Menu')}  disabled={isLoading} />
              <Rc2Key icon={GLYPHS.back}     label="Back"   color="#1976D2" onClick={() => sendKeyEvent(4,  'Back')}  disabled={isLoading} />
              <Rc2Key icon={GLYPHS.ok}       label="OK"     color="#2E7D32" onClick={() => sendKeyEvent(23, 'OK')}    disabled={isLoading} />

              <Rc2Key icon={GLYPHS.rotate}   label="Reload" color="#F57C00" onClick={() => handleDeviceAction('Reload App')}    disabled={isLoading} />
              <Rc2Key icon={GLYPHS.reboot}   label="Reboot" color="#FB8C00" onClick={() => handleDeviceAction('Reboot Device')} disabled={isLoading} />
              <Rc2Key icon={GLYPHS.moonZ}    label="Sleep"  color="#5E35B1" onClick={() => handleDeviceAction('Toggle Sleep')}  disabled={isLoading} />
              <Rc2Key icon={GLYPHS.power}    label="Power"  color="#C62828" onClick={handlePowerButton}                         disabled={isLoading} />
            </div>

          </div>

          {/* 2026-05-18 — Display Now restyled to match RemoteControlModal.
              Same four rows: flash message · layout · media file · playback.
              "Back to scheduled" 🕒 buttons next to layout and media revert
              the group to whatever the agent's scheduler says it should be
              playing. Reuses .rc2-* classes from RemoteControlModal.css. */}
          <div className="remote-control-section rc2-display">
            <div className="rc2-display-head">
              <h3 className="rc2-h3">Display now</h3>
              <div className="rc2-duration">
                <label htmlFor="gc-duration-in">Duration (s)</label>
                <input
                  id="gc-duration-in"
                  type="number" min="1" max="300"
                  value={displayLength}
                  onChange={(e) => setDisplayLength(parseInt(e.target.value) || 1)}
                  disabled={isLoading}
                  className="rc2-num"
                />
              </div>
            </div>

            {/* Row 1: flash message */}
            <div className="rc2-row">
              <div className="rc2-row-icon" title="Flash message">💬</div>
              <input
                type="text"
                value={textMessage}
                onChange={(e) => setTextMessage(e.target.value)}
                placeholder="Type a message to flash on screen…"
                disabled={isLoading}
                className="rc2-input"
              />
              <button
                type="button"
                className="rc2-apply"
                onClick={handleShowMessage}
                disabled={isLoading || !textMessage.trim()}
                title="Send to all online devices in this group"
                aria-label="Send message"
              >✓</button>
              <span className="rc2-spacer" />
            </div>

            {/* Row 2: layout */}
            <div className="rc2-row">
              <div className="rc2-row-icon" title="Layout">🗂</div>
              <select
                className="rc2-select"
                value={selectedLayout}
                onChange={(e) => setSelectedLayout(e.target.value)}
                disabled={isLoading || layouts.length === 0}
              >
                <option value="">{layouts.length === 0 ? 'No layouts' : 'Select layout…'}</option>
                {layouts.map((l) => {
                  const name = typeof l === 'string' ? l : l.name;
                  return <option key={name} value={name}>{name}</option>;
                })}
              </select>
              <button
                type="button"
                className="rc2-apply"
                onClick={handleSetLayout}
                disabled={isLoading || !selectedLayout}
                title="Activate selected layout on all online devices"
                aria-label="Activate layout"
              >✓</button>
              <button
                type="button"
                className="rc2-back"
                onClick={handleClearLayout}
                disabled={isLoading}
                title="Back to scheduled layout"
                aria-label="Back to scheduled layout"
              >🕒</button>
            </div>

            {/* Row 3: media file */}
            <div className="rc2-row">
              <div className="rc2-row-icon" title="Media file">🎞</div>
              <select
                className="rc2-select"
                value={selectedFile}
                onChange={(e) => setSelectedFile(e.target.value)}
                disabled={isLoading || groupFiles.length === 0}
              >
                <option value="">{groupFiles.length === 0 ? 'No media files' : 'Select media file…'}</option>
                {groupFiles.map((file) => (
                  <option key={file.name} value={file.name}>{file.name}</option>
                ))}
              </select>
              <button
                type="button"
                className="rc2-apply"
                onClick={handleShowFile}
                disabled={isLoading || !selectedFile}
                title="Show this file on all online devices"
                aria-label="Show file"
              >✓</button>
              <button
                type="button"
                className="rc2-back"
                onClick={handleClearLayout}
                disabled={isLoading}
                title="Back to scheduled content"
                aria-label="Back to scheduled content"
              >🕒</button>
            </div>

            {/* Row 4: media-playback target + prev / play-pause / next */}
            <div className="rc2-playback-row">
              <select
                value={mediaControlTarget}
                onChange={(e) => setMediaControlTarget(e.target.value)}
                disabled={isLoading}
                className="rc2-select rc2-select-target"
              >
                <option value="background">Background Music</option>
                <option value="main">Main zone</option>
              </select>
              <button type="button" className="rc2-apply rc2-pb"              onClick={() => handleMediaControl('prev')}       disabled={isLoading} title="Previous" aria-label="Previous">⏮</button>
              <button type="button" className="rc2-apply rc2-pb rc2-pb-play" onClick={() => handleMediaControl('play_pause')} disabled={isLoading} title={isPaused ? 'Resume' : 'Pause'} aria-label="Play/Pause">{isPaused ? '▶' : '⏸'}</button>
              <button type="button" className="rc2-apply rc2-pb"              onClick={() => handleMediaControl('next')}       disabled={isLoading} title="Next"     aria-label="Next">⏭</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupControlModal;
